<template>
	<div id="recap" ref="recap" class="row-padding col-padding"> 
		<div class="actions flex space v-center">
			<div class="go-back" @click="$emit('back')">
				<span class="material-symbols-rounded">arrow_left_alt</span>
			</div>
		</div>
		
		<div v-if="current_preset" class="container flex gap-l">
			<div class="data flex column gap-xs">
				<div class="title">
					<div class="very-large-font uppercase">{{ getLocale('recap') }}</div>
				</div>
				
				<div class="flex gap-m">
					<div>
						<div class="small-font uppercase">{{ getLocale('collection') }}</div>
						<div>KILIM</div>
					</div>
					<div>
						<div class="small-font uppercase">{{ getLocale('thickness') }}</div>
						<div>9 mm</div>
					</div>
					<div>
						<div class="small-font uppercase">{{ getLocale('material') }}</div>
						<div>{{ getLocale('gres') }}</div>
					</div>
				</div>
				
				<div class="area-table flex column gap-s">
					<div class="flex column gap-s">
						<div class="large-font">{{ getLocale('calculate_quantities') }}</div>
						
						<div class="inputs flex column gap-xs">
							<div class="uppercase">{{ getLocale('insert_dimensions') }}</div>
							
							<div class="fields flex space gap-s v-center">
								<Switch 
									v-model="unit"
									@change="setUnit"
									:options="[
										{ text: 'CM', value: 'cm' }, 
										{ text: 'INCH', value: 'inch' }, 
									]"
								/>
								
								<div class="flex gap-s">
									<div class="input-wrapper">
										<label for="width">{{ getLocale('width') }}</label>
										<input v-model="width" name="width" id="width" type="text" />
									</div>
									
									<div class="input-wrapper">
										<label for="height">{{ getLocale('height') }}</label>
										<input v-model="height" name="height" id="height" type="text" />
									</div>
								</div>
							</div>
						</div>
						
						<div class="products">
							<table>
								<thead class="small-font">
									<tr>
										<th>{{ getLocale('product') }}</th>
										<th>{{ getLocale('boxes') }}</th>
										<th>{{ unit == 'cm' ? 'MQ' : 'SQ FT'}}</th>
										<th>{{ getLocale('scrap') }}</th>
									</tr>
								</thead>
								<tbody>
									<tr 
										v-for="(g,i) in pdf_data" 
										:class="{
											custom : isCustom(current_preset.groups[i]),
											lowmq : g[3] < 6,
										}"
									>
										<td>
											<div class="flex v-center gap-xxs">
												<svg class="color" :style="{backgroundColor: g[0][0]}">
													<g v-if="g[0].length > 1">
														<path 
															v-for="(color,i) in g[0]"
															:d="getPieSlice(i, g[0].length)"
															:fill="color"
														/>
													</g>
												</svg>
												
												<div class="flex v-center gap-xxs">
													<div class="collection-name">Kilim</div>
													
													<div class="names flex v-center gap-xxs">
														<span v-for="d in g[1]">
															{{ d }}
														</span>
													</div>
												</div>
											</div>
										</td>
										<td>{{ g[2] }}</td>
										<td>{{ (g[3] * ( unit == 'cm' ? 1 : 10.764)).toFixed(2) }}</td>
										<td>7%</td>
									</tr>
								</tbody>
							</table>
							
							<div v-if="show_warning" class="min-availability small-font">
								{{ getLocale('minimum_availability') }}
							</div>
						</div>
					</div>
					
					<div class="small-font">
						{{ getLocale('recap_warning_1') }}<br>{{ getLocale('recap_warning_2') }}
					</div>
				</div>
				
				<div class="buttons flex space gap-s wrap">
					<div class="flex gap-s">
						<Button outlined :disabled="loading" @click="saveImage">
							&nbsp;{{ getLocale('laying_pattern') }} <span class="material-symbols-rounded">download</span>
						</Button>
						
						<Button outlined :disabled="loading" @click="requestPdf">
							&nbsp;{{ getLocale('pdf_recap') }}<span class="material-symbols-rounded">download</span>
						</Button>
					</div>
					
					<div class="flex gap-s">
						<Button secondary :disabled="loading" @click="startShare">
							&nbsp;{{ getLocale('share') }}<span class="material-symbols-rounded">share</span>
						</Button>
						
						<Button @click="scrollDown">
							&nbsp;{{ getLocale('contact_us') }}<span class="material-symbols-rounded">arrow_downward_alt</span>
						</Button>
					</div>
				</div>
			</div>
			
			<div class="preview" ref="box">
				<div v-if="parsed_width > 0 && parsed_height > 0">
					<svg 
						ref="svg"
						:width="svg_size[0]"
						:height="svg_size[1]"
						:style="{
							stroke : background_color,
							backgroundRepeat: 'repeat',
							backgroundImage: 'url('+sample+')',
							backgroundSize: (((parseFloat(current_preset.width)+gap)*svg_size[0])/width_limit)+'px',
						}"
					>
					</svg>
						
					<div v-if="width_cm > width_limit" class="overflow-x" :style="{
						height : svg_size[1]+'px',
						right : (((box_width - svg_size[0]) / 2 ) -1) +'px'
					}"></div>
					
					<div v-if="height_cm > height_limit" class="overflow-y" :style="{
						width : svg_size[0]+'px',
						bottom : (((box_height - svg_size[1]) / 2 ) -1) +'px'
					}"></div>
					
					<div  class="measure-x" :style="{
						width : svg_size[0]+'px',
						top : (((box_height - svg_size[1]) / 2 ) -15) +'px'
					}">
						<div class="text">
							{{ (width_limit / (unit == 'cm' ? 1 : 2.54)).toFixed(0) }}{{ unit }}
							<span v-if="width_cm > width_limit"> / {{ parsed_width }}{{ unit }}</span>
						</div>
					</div>
					
					<div class="measure-y" :style="{
						height : svg_size[1]+'px',
						left : (((box_width - svg_size[0]) / 2 ) -15) +'px'
					}">
						<div class="text">
							{{ (height_limit / (unit == 'cm' ? 1 : 2.54)).toFixed(0) }}{{ unit }}
							<span v-if="height_cm > height_limit"> / {{ parsed_height }}{{ unit }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		
		<div class="contact-form col-8 center" :class="{loading : loading}">
			<slide-up-down :active="!form_success" :duration="300">
				<h4 class="very-large-font text-center">{{ getLocale('contact_us') }}</h4>
				
				<div class="fields flex wrap gap-s">
					<input v-model="form_data.first_name" type="text" :placeholder="getLocale('first_name')"/>
					<input v-model="form_data.last_name" type="text" :placeholder="getLocale('last_name')"/>
					<input v-model="form_data.phone" type="text" :placeholder="getLocale('phone')"/>
					<input v-model="form_data.mail" type="text" :placeholder="getLocale('email')"/>
					<textarea v-model="form_data.message" rows="4" :placeholder="getLocale('message')"/>
				</div>
					
				<div class="col-12 flex space v-center">
					<label><input v-model="consent" type="checkbox">{{ getLocale('privacy_concerns') }}</label>
					<Button :disabled="form_disabled" @click="sendRequest">
						&nbsp;{{ getLocale('send') }} <span class="material-symbols-rounded">arrow_right_alt</span>
					</Button>
				</div>
			</slide-up-down >
			
			<slide-up-down :active="form_success" :duration="300">
				<div class="text-center">
					<h4 class="very-large-font">{{ getLocale('thanks') }}</h4>
					{{ getLocale('thanks_message') }}
				</div>
			</slide-up-down >
		</div>
		
		<transition name="fade">
			<div v-if="share_modal" class="share-modal">
				<div class="backdrop" @click="closeModal"></div>
				
				<div class="container">
					<div class="modal-header flex gap-m space v-center">
						<h4 class="large-font">{{ getLocale('share') }}</h4>
						
						<Button secondary :disabled="share_link == null" @click="closeModal">
							<span class="material-symbols-rounded">close</span>
						</Button>
					</div>
					
					<slide-up-down :active="share_link == null" :duration="300">
						<div class="loading-dots">
							<div class="dot"></div>
							<div class="dot"></div>
							<div class="dot"></div>
						</div>
					</slide-up-down >
					
					<slide-up-down :active="share_link != null" :duration="300">
						<div class="col-12 flex space v-center">
							<a :href="share_link" target="_blank">{{ getLocale('share_link') }}</a>
							
							<Button secondary @click="copyToClipboard">
								<span class="material-symbols-rounded">share</span>
							</Button>
						</div>
					</slide-up-down >
				</div>
			</div>
		</transition>
		
		<transition name="fade">
			<div v-if="loading" class="loader">
				<div class="loading-dots">
					<div class="dot"></div>
					<div class="dot"></div>
					<div class="dot"></div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
	import { commonMixin } from '@/mixins/common.js';
	import { mapState, mapMutations } from 'vuex'
	import Switch from '@/components/Switch.vue'
	import Button from '@/components/Button.vue'
	import SlideUpDown from 'vue-slide-up-down'
	
	import { saveSvgAsPng, svgAsPngUri } from 'save-svg-as-png';
	
	export default {
		name: 'Recap',
		components: { Switch, Button, SlideUpDown },
		props: ['sample'],
		mixins: [ commonMixin ],
		data: function () { return {
			loading : false,
			width : window.creation ? parseInt(window.creation.width) : window.language == 'en' ? 100 : 300,
			height : window.creation ? parseInt(window.creation.height) : window.language == 'en' ? 100 : 300,
			scale : 4,
			form_data : {
				first_name : '',
				last_name : '',
				phone : '',
				mail : '',
				message : ''
			},
			consent : false,
			form_success : false,
			share_modal : false,
			share_link : null,
			box_width : 0,
			box_height : 0,
			limit : 10000000
		}},
		computed: {
			...mapState([ 'base_url', 'tiles', 'current_preset', 'bkg_colors', 'background_color', 'gap', 'unit' ]),
			parsed_width(){
				if(this.width == '') return 0;
				return parseInt(this.width.toString().replace(/\D/g, ""));
			},
			parsed_height(){
				if(this.height == '') return 0;
				return parseInt(this.height.toString().replace(/\D/g, ""));
			},
			width_cm(){
				return this.parsed_width * ((this.unit == 'inch') ? 2.54 : 1);
			},
			height_cm(){
				return this.parsed_height * ((this.unit == 'inch') ? 2.54 : 1);
			},
			width_limit(){
				return Math.min(this.width_cm, this.limit);
			},
			height_limit(){
				return Math.min(this.height_cm, this.limit);
			},
			total_tiles(){
				let min_tiles_x = Math.ceil(this.width_cm / parseFloat(this.current_preset.width));
				let min_tiles_y = Math.ceil(this.height_cm / parseFloat(this.current_preset.height));
				
				return min_tiles_x * min_tiles_y;
			},
			tile_limits(){
				let count_x = Math.ceil(this.width_limit/(parseFloat(this.current_preset.width)));
				let count_y = Math.ceil(this.height_limit/(parseFloat(this.current_preset.height)));
				return [count_x, count_y];
			},
			form_disabled(){
				let fd = this.form_data;
				return this.consent == false ||
					   fd.first_name == '' ||
					   fd.last_name == '' ||
					   fd.mail == '' ||
					   fd.message == ''; 
			},
			pdf_data(){
				let data = [];
				
				this.current_preset.groups.forEach(g => {
					let group_data = [];
					
					let descriptions = [];
					g.tile.data.forEach((s,i) => {
						let type = g.types[i] == 'gloss' ? 'glossy' : 'matt';
						descriptions.push( this.findColor(g.tile.info.colors, g.colors[i]).title +' '+ type )
					});
					
					let product_code = 'CUSTOM';
					
					if(g.colors.length == 1){
						let color = this.findColor(g.tile.info.colors, g.colors[0])
						let type = g.types[0] == 'matt' ? 'Matt' : 'Glossy';
						let category = color.categories.find( c => {
							return c.name_category == type;
						});
						
						if( typeof category != 'undefined' ){
							product_code = category.product_id;
						}
					}
					
					group_data.push(g.colors);
					group_data.push(descriptions);
					group_data.push(Math.ceil((this.total_tiles * g.tiles.length) / g.tile.piece_per_box));
					group_data.push((this.tileArea(g.tile_id) * this.total_tiles * g.tiles.length).toFixed(3));
					group_data.push(product_code);
					
					data.push(group_data);
				})
				return data;
			},
			show_warning(){
				let show = false;
				
				this.pdf_data.forEach((g,i) => {
					if( this.isCustom(this.current_preset.groups[i]) && g[3] < 6){
						show = true;
					}
				});
				
				return show;
			},
			svg_size(){
				let svg_ratio = this.width_limit/this.height_limit;
				let box_ratio = this.box_width/this.box_height;
				let svg_width, svg_height;
				
				if(svg_ratio >= box_ratio){
					svg_width = this.box_width;
					svg_height = svg_width/svg_ratio;
				}else{
					svg_height = this.box_height;
					svg_width = svg_height * svg_ratio;
				}
				
				return [svg_width, svg_height];
			}
		},
		mounted(){
			window.addEventListener('resize', this.setBoxDimension);
			window.addEventListener('orientationchange', this.setBoxDimension);
			this.setBoxDimension();
		},
		methods: {
			...mapMutations([ 'setUnit' ]),
			setBoxDimension(){
				if(this.$refs.box){
					this.box_width = this.$refs.box.offsetWidth;
					this.box_height = this.$refs.box.offsetHeight;
				}
			},
			scrollDown(){
				this.$refs['recap'].scrollTo({
					top: document.body.scrollHeight * 10,
					behavior: 'smooth'
				});
			},
			tileArea(tile_id){
				let tile = this.tiles.find(t => t.external_id == tile_id);
				let area = parseFloat(tile.width) * parseFloat(tile.height);
				return area/10000;
			},
			getCreationData(){
				let groups = [];
				return {
					pattern_id : this.current_preset.ID,
					background_color : this.background_color,
					groups : this.current_preset.groups.map( g => {
						return {
							colors : g.colors,
							types : g.types,
						};
					})
				};
			},
			async startShare(){
				this.share_modal = true;
				this.share_link = null;
				
				let data = {
					method: 'POST', 
					headers: { 'Content-Type': 'application/json'  },
					body: JSON.stringify({
						unit : this.unit,
						width : this.width,
						height : this.height,
						creation_data : this.getCreationData(),
						form_data : {}
					})
				};
				
				let response = await fetch(this.base_url+'qzr-api/share', data)
				let result = await response.json();
				
				this.share_link = result;

				if(navigator.share){
					await navigator.share({
						title: 'Titolo della Pagina',
						text: 'Descrizione opzionale del contenuto',
						url: this.share_link
					});
				}
			},
			copyToClipboard(){
				navigator.clipboard.writeText(this.share_link);
			},
			closeModal(){
				if(this.share_link != null){
					this.share_modal = false;
				}
			},
			saveImage(){
				saveSvgAsPng(this.$refs.svg, "schema di posa.png");
			},
			requestPdf(){
				this.loading = true;
				
				svgAsPngUri(this.$refs.svg, { scale: 1 }).then(uri => {
					let data = {
						method: 'POST', 
						headers: { 'Content-Type': 'application/json'  },
						body: JSON.stringify({
							unit : this.unit,
							width : this.width,
							height : this.height,
							creation_data : this.getCreationData(),
							form_data : this.form_data,
							pdf_data : this.pdf_data,
							image : uri
						})
					};
				
					fetch(this.base_url+'qzr-api/pdf', data)
						.then(response => response.json())
						.then(data => {
							this.loading = false;
							
							const source = 'data:application/pdf;base64,'+data;
							const download = document.createElement("a");
							
							download.href = source;
							download.download = 'riepilogo.pdf';
							download.click();
						});
				});
			},
			sendRequest(){
				this.loading = true;
				
				svgAsPngUri(this.$refs.svg, { scale: 1 }).then(uri => {
					let data = {
						method: 'POST', 
						headers: { 'Content-Type': 'application/json'  },
						body: JSON.stringify({
							unit : this.unit,
							width : this.width,
							height : this.height,
							creation_data : this.getCreationData(),
							form_data : this.form_data,
							pdf_data : this.pdf_data,
							image : uri
						})
					};
				
					fetch(this.base_url+'qzr-api/form', data)
						.then(response => response.json())
						.then(data => {
							this.loading = false;
							this.form_success = true;
						});
				});
			}
		}
	}
</script>

<style scoped lang="scss">
	#recap{
		position: absolute;
		top: -30px;
		right: -100%;
		width: 100%;
		height: calc(100% + 30px);
		padding: 20px;
		padding-top: 70px;
		background: white;
		z-index: 10;
		overflow-y: auto;
		transition: right 0.3s;
		
		&.active{
			right: 0;
		}
		
		.actions{
			height: 40px;
			
			.go-back{
				cursor: pointer;
			}
		}
		
		@media (max-width: 999px) {
			.container{
				display: block;
				
				.data,
				.preview{
					width: 100%;
				}
				
				.preview{
					padding-top: 100%;
					width: calc(100% - 100px);
					margin: 50px;
				}
			}
		}
		
		.area-table{
			margin-top: 20px;
			padding: 20px;
			background: #E4E2DE;
			border-radius: 10px;
			
			@media (max-width: 479px) {
				.fields{
					display: block;
					
					.switch{
						margin-bottom: 10px;
					}
					
					.input-wrapper{
						font-size: 0.8rem;
					}
				}
			}
			
			input{
				width: 60px;
				text-align: center;
			}
			
			.products{
				padding: 20px;
				background: var(--white);
				border-radius: 10px;
				
				@media (max-width: 479px) {
					padding: 10px;
					margin-left: -20px;
					margin-right: -20px;
					border-radius: 0;
				}
					
				.min-availability{
					text-transform: uppercase;
					color: #FF5E5E;
					margin-top: 10px;
				}
				
				table{
					width: 100%;
					text-align: center;
					border-collapse: collapse;
					
					th{
						font-weight: 400;
						text-transform: uppercase;
						border-bottom: 1px solid black;
						padding: 10px;
						
						@media (max-width: 479px) {
							padding: 5px;
						}
					}
					td{
						padding: 10px;
						white-space: nowrap;
						
						@media (max-width: 479px) {
							padding: 5px;
						}
						
						.color{
							width: 24px;
							height: 24px;
							border-radius: 50%;
							background: gray;
							cursor: pointer;
							transform: rotate(180deg);
						}
						span:not(:last-child):after{
							content: '+';
							margin-left: 8px;
							
							@media (max-width: 599px) {
								display: none;
							}
						}
							
						@media (max-width: 599px) {
							.collection-name{
								display: none;
							}
							
							.names{
								display: block;
								text-align: left;
								
								span{
									display: block;
								}
							}
						}
					}
					
					.custom.lowmq td:not(:first-child){
						color: #FF5E5E;
					}
				}
			}
		}
		
		.data{
			position: relative;
			width: calc(50% - 30px);
			flex-shrink: 0;
		}
		.preview{
			position: relative;
			width: calc(50% - 30px);
			flex-shrink: 0;
			
			svg{
				position: absolute;  
				left: 50%;		
				top: 50%;
				max-height: 100%;
				transform: translate(-50%, -50%);
			}
				
				.overflow-x{
					position: absolute;
					top: 50%;
					width: 200px;
					transform: translateY(-50%);
					background: linear-gradient(to right, transparent, white);
				}
				
				.overflow-y{
					position: absolute;
					left: 50%;
					height: 200px;
					transform: translateX(-50%);
					background: linear-gradient(to bottom, transparent, white);
				}
				
				.measure-x{
					position: absolute;
					left: 50%;
					transform: translateX(-50%);
					border-bottom: 1px solid black;
					
					.text{
						position: absolute;
						bottom: 0;
						white-space: nowrap;
						
						span{
							color: #FF5E5E;
						}
					}
					
					&:after{
						content: '';
						position: absolute;
						top: -2.5px;
						right: 0;
						width: 5px;
						height: 5px;
						border-bottom: 1px solid black;
						border-right: 1px solid black;
						transform: rotate(-45deg);
					}
				}
				
				.measure-y{
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					border-right: 1px solid black;
					
					.text{
						position: absolute;
						right: 0;
						white-space: nowrap;
						writing-mode: sideways-lr;
						
						span{
							color: #FF5E5E;
						}
					}
					
					&:after{
						content: '';
						position: absolute;
						left: -2.5px;
						bottom: 0;
						width: 5px;
						height: 5px;
						border-bottom: 1px solid black;
						border-right: 1px solid black;
						transform: rotate(45deg);
					}
				}
		}
		
		.buttons{
			margin-top: 20px;
		}
		
		.contact-form{
			margin: 100px auto;
			opacity: 1;
			transition: opacity 0.2s;
			
			@media (max-width: 599px) {
				width: 100%;
			}

			&.loading{
				opacity: 0.3;
				pointer-events: none;
			}
			
			h4{
				margin-bottom: 20px;
			}
			
			.fields{
				margin-bottom: 20px;
				
				input{
					width: calc(50% - 10px);
				}
				textarea{
					width: 100%;
				}
				
				input,textarea{
					font-family: "Poppins", sans-serif;
					border: none;
					border-bottom: 1px solid black;
					border-radius: 0;
					padding: 10px;
					font-size: 1rem;
					outline: none;
					resize: none; 
				}
			}
				
			input[type=checkbox]{
				margin-right: 10px;
				
			}
		}
		
		.share-modal{
			position: absolute;
			inset: 0;
			
			.backdrop{
				position: absolute;
				inset: 0;
				background: rgba(0,0,0,0.3);
			}
			.container{
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%,-50%);
				background: white;
				border-radius: 20px;
				padding: 20px;
				text-align: center;
				width: 300px;
				
				.loading-dots{
					display: inline-block;
					margin: 30px;
				}
				.col-12{
					margin-top: 20px;
					
					a{
						color: inherit;
					}
				}
			}
			
		}
	
		.loader{
			position: fixed;
			left: 0;
			width: 100%;
			top: 60px;
			height: calc(100% - 60px);
			z-index: 10;
			background: rgba(255,255,255,0.7);
			
			.loading-dots{
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
			}
		}
	}
</style>
